import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSupplierService } from 'services/suppliers';
import { useSuppliersData } from 'hooks/useSuppliersData';
import { Supplier } from 'types/supplier';
import Button from 'components/Button';
import Toast from 'components/Toast';
import RefreshButton from 'components/RefreshButton';
import Pagination from 'components/Pagination';
import { Plus, Search, Eye, Pencil, Trash2, RotateCcw } from 'lucide-react';
import AddSupplierModal from './AddSupplierModal';
import EditSupplierModal from './EditSupplierModal';
import DeleteSupplierModal from './DeleteSupplierModal';
import ViewSupplierModal from './ViewSupplierModal';
import styles from './Suppliers.module.scss';

function Suppliers() {
  const supplierService = useSupplierService();
  const [includeVoided, setIncludeVoided] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [supplierToEdit, setSupplierToEdit] = useState<Supplier | null>(null);
  const [supplierToDelete, setSupplierToDelete] = useState<Supplier | null>(
    null,
  );
  const [supplierToView, setSupplierToView] = useState<Supplier | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Use suppliers data hook
  const {
    suppliers,
    loading,
    reloading,
    error,
    reload,
    addSupplier,
    updateSupplier,
  } = useSuppliersData({ includeVoided });

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  // Show error toast if there's an error
  useEffect(() => {
    if (error) {
      showToast(error, 'error');
    }
  }, [error, showToast]);

  // Filter suppliers based on search
  const filteredSuppliers = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return suppliers.filter((supplier) => {
      const matchesSearch =
        supplier.name.toLowerCase().includes(searchLower) ||
        (supplier.email?.toLowerCase().includes(searchLower) ?? false) ||
        (supplier.phone?.toLowerCase().includes(searchLower) ?? false) ||
        (supplier.address?.toLowerCase().includes(searchLower) ?? false);
      return matchesSearch;
    });
  }, [suppliers, searchQuery]);

  const totalFilteredSuppliers = filteredSuppliers.length;

  const paginatedSuppliers = useMemo(() => {
    if (totalFilteredSuppliers === 0) {
      return [];
    }
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredSuppliers.slice(startIndex, endIndex);
  }, [filteredSuppliers, currentPage, pageSize, totalFilteredSuppliers]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, includeVoided]);

  useEffect(() => {
    const nextTotalPages = Math.max(
      1,
      Math.ceil(totalFilteredSuppliers / pageSize),
    );
    setCurrentPage((prevPage) => Math.min(prevPage, nextTotalPages));
  }, [totalFilteredSuppliers, pageSize]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleAddSuccess = useCallback(
    (supplier: Supplier) => {
      addSupplier(supplier);
      setIsAddModalOpen(false);
      showToast('Supplier created successfully', 'success');
    },
    [addSupplier, showToast],
  );

  const handleEditSuccess = useCallback(
    (supplier: Supplier) => {
      updateSupplier(supplier);
      setSupplierToEdit(null);
      showToast('Supplier updated successfully', 'success');
    },
    [updateSupplier, showToast],
  );

  const handleDeleteSuccess = useCallback(
    (supplier: Supplier) => {
      // When voiding, we update the supplier with isVoided = true
      updateSupplier(supplier);
      setSupplierToDelete(null);
      showToast('Supplier voided successfully', 'success');
    },
    [updateSupplier, showToast],
  );

  const handleRestore = useCallback(
    async (supplier: Supplier) => {
      setIsDeleting(true);
      try {
        const restoredSupplier = await supplierService.restoreSupplier(
          supplier.id,
        );
        updateSupplier(restoredSupplier);
        showToast('Supplier restored successfully', 'success');
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to restore supplier. Please try again.';
        showToast(errorMessage, 'error');
      } finally {
        setIsDeleting(false);
      }
    },
    [supplierService, updateSupplier, showToast],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Suppliers</h1>
        <div className={styles.headerButtons}>
          <RefreshButton onClick={reload} loading={reloading} />
          <Button onClick={() => setIsAddModalOpen(true)}>
            <Plus size={18} style={{ marginRight: '0.5rem' }} />
            New Supplier
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.searchContainer}>
          <Search size={18} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search suppliers..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            className={styles.searchInput}
          />
        </div>
        <label className={styles.filterLabel}>
          <input
            type="checkbox"
            checked={includeVoided}
            onChange={(e) => {
              setIncludeVoided(e.target.checked);
            }}
            className={styles.filterCheckbox}
          />
          Include Voided
        </label>
      </div>

      {/* Error State */}
      {error && suppliers.length === 0 && (
        <div className={styles.errorState}>
          <p>{error}</p>
          <Button onClick={reload}>Retry</Button>
        </div>
      )}

      {/* Suppliers Table */}
      {loading ? (
        <div className={styles.tableContainer}>
          <div className={styles.tableSkeleton}>
            <div className={styles.tableSkeletonHeader}>
              {['Name', 'Email', 'Phone', 'Address', 'Status', 'Actions'].map(
                (label) => (
                  <div key={label} className={styles.tableSkeletonHeaderCell}>
                    <span
                      className={`${styles.skeletonLine} ${styles.skeletonMedium}`}
                    />
                  </div>
                ),
              )}
            </div>
            <div className={styles.tableSkeletonBody}>
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={`supplier-skeleton-${index}`}
                  className={styles.skeletonRow}
                >
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonLong}`}
                  />
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonMedium}`}
                  />
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonShort}`}
                  />
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonLong}`}
                  />
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonShort}`}
                  />
                  <span
                    className={`${styles.skeletonLine} ${styles.skeletonShort}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : suppliers.length > 0 ? (
        <div className={styles.tableContainer}>
          {reloading && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingSpinner}>Loading...</div>
            </div>
          )}
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Address</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSuppliers.map((supplier) => (
                <tr key={supplier.id}>
                  <td className={styles.nameCell}>{supplier.name}</td>
                  <td className={styles.emailCell}>{supplier.email || '-'}</td>
                  <td className={styles.phoneCell}>{supplier.phone || '-'}</td>
                  <td className={styles.addressCell}>
                    {supplier.address || '-'}
                  </td>
                  <td className={styles.statusCell}>
                    {supplier.isVoided ? (
                      <span className={styles.voidedBadge}>Voided</span>
                    ) : (
                      <span className={styles.activeBadge}>Active</span>
                    )}
                  </td>
                  <td className={styles.actionsCell}>
                    <button
                      className={styles.viewButton}
                      onClick={() => {
                        setSupplierToView(supplier);
                      }}
                      aria-label={`View supplier ${supplier.id}`}
                      title="View supplier"
                    >
                      <Eye size={16} />
                    </button>
                    {!supplier.isVoided && (
                      <button
                        className={styles.editButton}
                        onClick={() => {
                          setSupplierToEdit(supplier);
                        }}
                        aria-label={`Edit supplier ${supplier.id}`}
                        title="Edit supplier"
                      >
                        <Pencil size={16} />
                      </button>
                    )}
                    {!supplier.isVoided && (
                      <button
                        className={styles.voidButton}
                        onClick={() => {
                          setSupplierToDelete(supplier);
                        }}
                        aria-label={`Void supplier ${supplier.id}`}
                        title="Void supplier"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                    {supplier.isVoided && (
                      <button
                        className={styles.restoreButton}
                        onClick={() => {
                          void handleRestore(supplier);
                        }}
                        aria-label={`Restore supplier ${supplier.id}`}
                        title="Restore supplier"
                        disabled={isDeleting}
                      >
                        <RotateCcw size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Empty State */}
      {!loading && filteredSuppliers.length === 0 && !error && (
        <div className={styles.emptyState}>
          <p>
            {searchQuery || includeVoided
              ? 'No suppliers found matching your filters.'
              : 'No suppliers yet. Create your first supplier to get started.'}
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalFilteredSuppliers > 0 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            currentPage={currentPage}
            totalItems={totalFilteredSuppliers}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => {
            setToast(null);
          }}
        />
      )}

      {/* Modals */}
      <AddSupplierModal
        show={isAddModalOpen}
        onHide={() => setIsAddModalOpen(false)}
        onSuccess={handleAddSuccess}
        onError={(message) => showToast(message, 'error')}
      />

      <EditSupplierModal
        show={!!supplierToEdit}
        onHide={() => setSupplierToEdit(null)}
        supplier={supplierToEdit}
        onSuccess={handleEditSuccess}
        onError={(message) => showToast(message, 'error')}
      />

      <DeleteSupplierModal
        show={!!supplierToDelete}
        onHide={() => setSupplierToDelete(null)}
        supplier={supplierToDelete}
        onSuccess={handleDeleteSuccess}
        onError={(message) => showToast(message, 'error')}
      />

      <ViewSupplierModal
        show={!!supplierToView}
        onHide={() => setSupplierToView(null)}
        supplier={supplierToView}
        onEdit={() => {
          setSupplierToView(null);
          if (supplierToView) {
            setSupplierToEdit(supplierToView);
          }
        }}
        onDelete={() => {
          setSupplierToView(null);
          if (supplierToView) {
            setSupplierToDelete(supplierToView);
          }
        }}
        onRestore={() => {
          if (supplierToView) {
            void handleRestore(supplierToView);
          }
        }}
        onError={(message) => showToast(message, 'error')}
      />
    </div>
  );
}

export default Suppliers;
